// Per-station equipment detail (mock), derived from station config + the live
// overview snapshot so КТП / inverters / strings stay consistent with the fleet
// numbers shown on /scada/dispatch.
import type { StationConfig } from './stations';
import type { StationTelemetry } from './telemetry';
import type { OpState } from '../tokens';

export interface InverterUnit {
  id: string;
  ktpId: string;
  status: OpState;          // ok | warning | alarm | offline | curtailed
  pacKw: number;
  ppvKw: number;
  tempC: number;
  dailyKwh: number;
  strings: number;
  stringsFault: number;
}

export interface KtpSection {
  id: string;
  name: string;
  status: OpState;
  voltageKv: number;
  currentA: number;
  tempC: number;
  alarms: number;
  inverterIds: string[];
}

export interface StationDetail {
  ktps: KtpSection[];
  inverters: InverterUnit[];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const jit = (b: number, p: number) => b * (1 + (Math.random() * 2 - 1) * p);

export function buildStationDetail(cfg: StationConfig, t: StationTelemetry): StationDetail {
  const count = t.inverterCount || cfg.inverterCount;
  const ktpCount = clamp(Math.round(count / 20), 1, 6);
  const perKtp = Math.ceil(count / ktpCount);

  const offlineAll = t.state === 'offline';
  const working = offlineAll ? 0 : t.invertersWorking;
  const perInvPac = working > 0 ? t.activePowerKw / working : 0;

  const inverters: InverterUnit[] = [];
  for (let i = 0; i < count; i++) {
    const ktpIdx = Math.floor(i / perKtp);
    const ktpId = `ktp-${ktpIdx + 1}`;
    const isWorking = i < working;

    let status: OpState;
    if (offlineAll) status = 'offline';
    else if (!isWorking) status = i === working ? 'alarm' : 'offline';
    else if (t.limitReason) status = 'curtailed';
    else status = 'ok';

    const strings = cfg.dcCapacityKwp > 4000 ? 24 : 18;
    const stringsFault = status === 'ok' && Math.random() < 0.12 ? 1 : 0;
    const pac = isWorking ? Math.max(0, jit(perInvPac, 0.06)) : 0;

    inverters.push({
      id: `INV-${String(i + 1).padStart(2, '0')}`,
      ktpId,
      status,
      pacKw: Math.round(pac),
      ppvKw: Math.round(pac * 1.02),
      tempC: isWorking ? Math.round(jit(42, 0.1)) : 0,
      dailyKwh: isWorking ? Math.round(jit(t.dailyEnergyKwh / Math.max(1, working), 0.05)) : 0,
      strings,
      stringsFault,
    });
  }

  const ktps: KtpSection[] = [];
  for (let k = 0; k < ktpCount; k++) {
    const id = `ktp-${k + 1}`;
    const kids = inverters.filter((inv) => inv.ktpId === id);
    const anyAlarm = kids.some((inv) => inv.status === 'alarm');
    const anyOffline = kids.some((inv) => inv.status === 'offline');
    const status: OpState = offlineAll ? 'offline' : anyAlarm ? 'warning' : anyOffline ? 'warning' : 'ok';
    ktps.push({
      id,
      name: `КТП-${k + 1}`,
      status,
      voltageKv: offlineAll ? 0 : +jit(10.4, 0.01).toFixed(2),
      currentA: Math.round(kids.reduce((s, inv) => s + inv.pacKw, 0) / 6),
      tempC: offlineAll ? 0 : Math.round(jit(48, 0.08)),
      alarms: anyAlarm ? 1 : 0,
      inverterIds: kids.map((inv) => inv.id),
    });
  }

  return { ktps, inverters };
}
